import { useState, useEffect } from "react";
import { useUser } from "../contexts/UserContext";
import { httpsCallable } from "firebase/functions";
import { functions } from "../constants/firebase";
import { ref, onValue, off, update, set } from "firebase/database";
import { database } from "../constants/firebase";
import { STRIPE_SUPPORTED_COUNTRIES } from "../constants/stripeCountries";
import { FiX } from "react-icons/fi";

/**
 * SellerOnboarding Component
 * Handles Stripe Connect onboarding for sellers
 * Allows Host users to set up their payout account
 * Supports all Stripe-enabled countries
 * Supports Paystack onboarding for South Africa
 */
const SellerOnboarding = ({ isOpen, onClose }) => {
    const { currentUser, refreshUser } = useUser();
    const [loading, setLoading] = useState(false);
    const [checkingStatus, setCheckingStatus] = useState(true);
    const [selectedCountry, setSelectedCountry] = useState("US");
    const [switchingMethod, setSwitchingMethod] = useState(false);



    // 💡 New fields for South Africa Paystack
    const [bankCode, setBankCode] = useState("");
    const [accountNumber, setAccountNumber] = useState("");
    const [banks, setBanks] = useState([]);

    // 🔒 Paystack secret key is NEVER used on the client. Bank listing and
    // subaccount creation are proxied through Cloud Functions that hold the key
    // server-side (see PAYSTACK_BACKEND_SPEC.md).

    useEffect(() => {
        const fetchBanks = async () => {
            try {
                const getPaystackBanks = httpsCallable(functions, "getPaystackBanks");
                const response = await getPaystackBanks();
                // Function returns the raw Paystack payload: { status, data: [ {name, code} ] }
                setBanks(response.data?.data || []);
            } catch (error) {
                console.error('Error fetching banks:', error);
            }
        };

        fetchBanks();
    }, []);

    useEffect(() => {
        if (isOpen && currentUser?.uid) {
            checkAccountStatus();
        }
    }, [isOpen, currentUser?.uid]);

    // Real-time listener for seller status updates
    useEffect(() => {
        if (!currentUser?.uid || !isOpen) return;
        const userRef = ref(database, `Users/${currentUser.uid}`);
        const unsubscribe = onValue(userRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                if (data.sellerOnboardingStatus === "complete" && data.payoutMethod === "paystack") {
                    if (data.paystackBank) setBankCode(data.paystackBank);
                    if (data.paystackAccountNumber) setAccountNumber(data.paystackAccountNumber);
                    setSelectedCountry("ZA");
                }

            }
        });
        return () => off(userRef, "value", unsubscribe);
    }, [currentUser?.uid, isOpen]);

    useEffect(() => {
        if (!currentUser) return;

        if (currentUser.payoutMethod === "paystack") {
            setSelectedCountry("ZA");
            setBankCode(currentUser.paystackBank || "");
            setAccountNumber(currentUser.paystackAccountNumber || "");
        } else if (currentUser.payoutMethod === "stripe") {
            setSelectedCountry("US");
            setBankCode("");
            setAccountNumber("");
        }
    }, [currentUser?.payoutMethod]);


    const checkAccountStatus = async () => {
        if (!currentUser?.uid) return;

        setCheckingStatus(true);
        try {
            const checkStatus = httpsCallable(functions, "checkConnectAccountStatus");
            const result = await checkStatus({ userId: currentUser.uid });
            // setAccountStatus(result.data);
        } catch (error) {
            console.error("Error checking account status:", error);
        } finally {
            setCheckingStatus(false);
        }
    };

    const handleStartOnboarding = async () => {
        if (!currentUser?.uid || !currentUser?.email) {
            window.showToast("User information missing", "error");
            return;
        }

        if (!selectedCountry) {
            window.showToast("Please select your country", "error");
            return;
        }

        // ✅ If South Africa, use Paystack flow
        if (selectedCountry === "ZA") {
            if (!bankCode || !accountNumber) {
                window.showToast("Please provide bank name and account number", "error");
                return;
            }
            await handlePaystackOnboarding();
            return;
        }

        setLoading(true);
        try {
            // Step 1: Create connected account if doesn't exist
            if (!currentUser?.stripeConnectedAccountId) {
                const createAccount = httpsCallable(functions, "createConnectedAccount");
                const accountResult = await createAccount({
                    userId: currentUser.uid,
                    email: currentUser.email,
                    country: selectedCountry, // Pass selected country
                });
                console.log("✅ Account created:", accountResult.data);
                // Wait a moment for database to update
                await new Promise(resolve => setTimeout(resolve, 1000));
            }

            // Step 2: Get onboarding link
            const createOnboardingLink = httpsCallable(functions, "createConnectOnboardingLink");
            // Get clean base URL without hash
            const baseUrl = `${window.location.protocol}//${window.location.host}`;
            console.log("🔗 Requesting onboarding link with URLs:", {
                refreshUrl: `${baseUrl}/#/my-products?onboarding=refresh`,
                returnUrl: `${baseUrl}/#/my-products?onboarding=complete`,
            });
            const result = await createOnboardingLink({
                userId: currentUser.uid,
                refreshUrl: `${baseUrl}/#/my-products?onboarding=refresh`,
                returnUrl: `${baseUrl}/#/my-products?onboarding=complete`,
            });
            console.log("✅ Onboarding link received:", result.data);

            // Step 3: Redirect to Stripe onboarding
            if (result.data.url) {
                await update(ref(database, `Users/${currentUser.uid}`), {
                    payoutMethod: "stripe",
                });
                await refreshUser();
                window.location.href = result.data.url;
            } else {
                throw new Error("Failed to get onboarding link");
            }
        } catch (error) {
            console.error("Error starting onboarding:", error);
            window.showToast(error.message || "Failed to start onboarding. Please try again.", "error");
        } finally {
            setLoading(false);
        }
    };

    const handlePaystackOnboarding = async () => {
        setLoading(true);
        try {
            // 🔒 Subaccount creation runs server-side. The Cloud Function creates the
            // Paystack subaccount AND writes the immutable audit record at
            // PaystackSubaccounts/{subaccountCode} (uid, email, bank, account number,
            // verification status, createdAt). See PAYSTACK_BACKEND_SPEC.md.
            const createPaystackSubaccount = httpsCallable(functions, "createPaystackSubaccount");
            // Contract (functions/index.js): snake_case in, raw Paystack payload out.
            const result = await createPaystackSubaccount({
                business_name: currentUser.username || "My Business",
                settlement_bank: bankCode, // user input from dropdown
                account_number: accountNumber, // user input
                percentage_charge: 0,
                email: currentUser.email,
            });

            const data = result.data || {};
            const subaccountCode = data?.data?.subaccount_code;
            console.log("🔍 Paystack subaccount result:", data);

            if (data.status && subaccountCode) {
                // Users/{uid} only holds the *current active pointer*; the durable
                // mapping history lives under PaystackSubaccounts/{code}.
                await update(ref(database, `Users/${currentUser.uid}`), {
                    paystackSubaccountCode: subaccountCode,
                    sellerOnboardingStatus: "complete",
                    sellerCanReceivePayments: true,
                    payoutMethod: "paystack",
                    paystackBank: bankCode,
                    paystackAccountNumber: accountNumber,
                    sellerCountry: selectedCountry,
                });
                await refreshUser();

                window.showToast("✅ Paystack account linked successfully!", "success");
            } else {
                console.error("Paystack Error:", data.message);
                window.showToast(data.message || "Paystack onboarding failed.", "error");
            }
        } catch (error) {
            console.error("❌ Paystack onboarding error:", error);
            window.showToast(error.message || "Paystack onboarding failed. Please try again.", "error");
        } finally {
            setLoading(false);
        }
    };


    const handleOpenDashboard = async () => {
        if (!currentUser?.uid) return;

        setLoading(true);
        try {
            const getDashboardLink = httpsCallable(functions, "getConnectDashboardLink");
            const result = await getDashboardLink({ userId: currentUser.uid });

            if (result.data.url) {
                window.open(result.data.url, "_blank");
            } else {
                throw new Error("Failed to get dashboard link");
            }
        } catch (error) {
            console.error("Error opening dashboard:", error);
            window.showToast("Failed to open dashboard. Please try again.", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleSwitchPayoutMethod = async () => {
        if (!currentUser?.uid) return;

        try {
            setLoading(true);

            if (currentUser.payoutMethod === "stripe") {
                // Switching to Paystack
                setSelectedCountry("ZA"); // default to ZA for Paystack

                await update(ref(database, `Users/${currentUser.uid}`), {
                    payoutMethod: "paystack",
                    sellerOnboardingStatus: "incomplete",
                    stripeConnectedAccountId: null,
                    sellerCanReceivePayments: false,
                    sellerCountry: 'ZA',
                });

                setAccountNumber("");
                setBankCode("");

                window.showToast("You can now set up your Paystack account.", "info");
            } else {
                // Switching to Stripe. Preserve the Paystack audit record — only
                // mark the active pointer detached, never delete the mapping.
                if (currentUser.paystackSubaccountCode) {
                    // Flip the audit record to "detached" server-side. The
                    // PaystackSubaccounts node is closed to direct client writes, so
                    // this goes through a Cloud Function (admin SDK). Failure here
                    // must not block clearing the pointer below.
                    try {
                        const markDetached = httpsCallable(functions, "markPaystackSubaccountDetached");
                        await markDetached({ subaccountCode: currentUser.paystackSubaccountCode });
                    } catch (e) {
                        console.error("Failed to mark Paystack subaccount detached:", e);
                    }
                }

                await update(ref(database, `Users/${currentUser.uid}`), {
                    payoutMethod: "stripe",
                    sellerOnboardingStatus: "incomplete",
                    sellerCanReceivePayments: false,
                    paystackSubaccountCode: null, // clears the active pointer only
                    paystackBank: null,
                    paystackAccountNumber: null,
                    sellerCountry: 'US',
                });

                window.showToast("You can now set up your Stripe account.", "info");
            }

            await refreshUser();
            setSwitchingMethod(false);
            setSelectedCountry(currentUser.payoutMethod === "stripe" ? "US" : "ZA");
        } catch (err) {
            console.error(err);
            window.showToast("Failed to switch payout method", "error");
        } finally {
            setLoading(false);
        }
    };


    const handleDetachPayoutMethod = async () => {
        if (!currentUser?.uid) return;

        try {
            setLoading(true);

            if (currentUser.payoutMethod === "stripe") {
                await update(ref(database, `Users/${currentUser.uid}`), {
                    stripeConnectedAccountId: null,
                    sellerOnboardingStatus: "incomplete",
                    sellerCanReceivePayments: false,
                    payoutMethod: null,
                });
                window.showToast("Stripe account detached successfully!", "success");
            } else if (currentUser.payoutMethod === "paystack") {
                // Preserve the Paystack audit record — only mark it detached.
                if (currentUser.paystackSubaccountCode) {
                    // Flip the audit record to "detached" server-side. The
                    // PaystackSubaccounts node is closed to direct client writes, so
                    // this goes through a Cloud Function (admin SDK). Failure here
                    // must not block clearing the pointer below.
                    try {
                        const markDetached = httpsCallable(functions, "markPaystackSubaccountDetached");
                        await markDetached({ subaccountCode: currentUser.paystackSubaccountCode });
                    } catch (e) {
                        console.error("Failed to mark Paystack subaccount detached:", e);
                    }
                }

                await update(ref(database, `Users/${currentUser.uid}`), {
                    paystackSubaccountCode: null, // clears the active pointer only
                    sellerOnboardingStatus: "incomplete",
                    sellerCanReceivePayments: false,
                    payoutMethod: null,
                    paystackBank: null,
                    paystackAccountNumber: null,
                });
                window.showToast("Paystack account detached successfully!", "success");
            } else {
                window.showToast("No payout method to detach", "info");
            }

            await refreshUser();
        } catch (err) {
            console.error(err);
            window.showToast("Failed to detach payout method", "error");
        } finally {
            setLoading(false);
        }
    };

    console.log("Selected Country: ", selectedCountry);



    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="bg-white dark:bg-[#2A2A2A] rounded-2xl w-full max-w-lg shadow-xl max-h-[80vh] flex flex-col">

                {/* Header - stays fixed */}
                <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex-shrink-0">
                    <h2 className="text-2xl font-bold text-black dark:text-white">Seller Payout Setup</h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        Connect your bank account to receive payouts
                    </p>
                </div>

                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto px-6 py-6 space-y-4">
                    {/* your content here */}
                    {/* for example: */}
                    {checkingStatus ? (
                        <div className="text-center py-8">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#327691] mx-auto"></div>
                            <p className="text-gray-500 dark:text-gray-400 mt-4">
                                Checking account status...
                            </p>
                        </div>
                    ) : (
                        <>
                            {/* Status Display */}
                            {currentUser?.sellerOnboardingStatus === 'complete' && (
                                <div className={`p-4 rounded-lg border-2 ${currentUser?.sellerOnboardingStatus === 'complete' && currentUser.sellerCanReceivePayments
                                    ? "bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700"
                                    : "bg-yellow-50 dark:bg-yellow-900/20 border-yellow-300 dark:border-yellow-700"
                                    }`}>
                                    <div className="flex items-start gap-3">
                                        <div className="flex-shrink-0 mt-1">
                                            {currentUser?.sellerOnboardingStatus === 'complete' && currentUser.sellerCanReceivePayments ? (
                                                <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                            ) : (
                                                <svg className="w-6 h-6 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                                </svg>
                                            )}
                                        </div>
                                        <div className="flex-1">
                                            <h3 className={`font-semibold ${currentUser?.sellerOnboardingStatus === 'complete' && currentUser.sellerCanReceivePayments
                                                ? "text-green-800 dark:text-green-200"
                                                : "text-yellow-800 dark:text-yellow-200"
                                                }`}>
                                                {currentUser?.sellerOnboardingStatus === 'complete' && currentUser.sellerCanReceivePayments
                                                    ? "✓ Account Setup Complete"
                                                    : "⚠ Setup Incomplete"}
                                            </h3>
                                            <p className={`text-sm mt-1 ${currentUser?.sellerOnboardingStatus === 'complete' && currentUser.sellerCanReceivePayments
                                                ? "text-green-700 dark:text-green-300"
                                                : "text-yellow-700 dark:text-yellow-300"
                                                }`}>
                                                {currentUser?.sellerOnboardingStatus === 'complete' && currentUser.sellerCanReceivePayments
                                                    ? "Your account is fully set up and ready to receive payouts!"
                                                    : "Please complete your account setup to start receiving payments."}
                                            </p>

                                        </div>
                                    </div>
                                </div>
                            )}
                            {/* {currentUser?.sellerOnboardingStatus === 'complete' && (
                                <div className={`p-4 rounded-lg border-2 ${currentUser?.sellerOnboardingStatus === 'complete' && currentUser.sellerCanReceivePayments
                                    ? "bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700"
                                    : "bg-yellow-50 dark:bg-yellow-900/20 border-yellow-300 dark:border-yellow-700"
                                    }`}>
                                    <div className="flex items-start gap-3">
                                        <div className="flex-shrink-0 mt-1">
                                            {currentUser?.sellerOnboardingStatus === 'complete' && currentUser.sellerCanReceivePayments ? (
                                                <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                            ) : (
                                                <svg className="w-6 h-6 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                                </svg>
                                            )}
                                        </div>
                                        <div className="flex-1">
                                            <h3 className={`font-semibold ${currentUser?.sellerOnboardingStatus === 'complete' && currentUser.sellerCanReceivePayments
                                                ? "text-green-800 dark:text-green-200"
                                                : "text-yellow-800 dark:text-yellow-200"
                                                }`}>
                                                {currentUser?.sellerOnboardingStatus === 'complete' && currentUser.sellerCanReceivePayments
                                                    ? "✓ Account Setup Complete"
                                                    : "⚠ Setup Incomplete"}
                                            </h3>
                                            <p className={`text-sm mt-1 ${currentUser?.sellerOnboardingStatus === 'complete' && currentUser.sellerCanReceivePayments
                                                ? "text-green-700 dark:text-green-300"
                                                : "text-yellow-700 dark:text-yellow-300"
                                                }`}>
                                                {currentUser?.sellerOnboardingStatus === 'complete' && currentUser.sellerCanReceivePayments
                                                    ? "Your account is fully set up and ready to receive payouts!"
                                                    : "Please complete your account setup to start receiving payments."}
                                            </p>
                                            <div className="mt-3 space-y-1 text-xs">
                                                <div className="flex items-center gap-2">
                                                    {currentUser.sellerCanReceivePayments ? (
                                                        <span className="text-green-600 dark:text-green-400">✓</span>
                                                    ) : (
                                                        <span className="text-gray-400">○</span>
                                                    )}
                                                    <span className={currentUser.sellerCanReceivePayments ? "text-green-700 dark:text-green-300" : "text-gray-600 dark:text-gray-400"}>
                                                        Payment processing enabled
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    {currentUser.sellerCanReceivePayments ? (
                                                        <span className="text-green-600 dark:text-green-400">✓</span>
                                                    ) : (
                                                        <span className="text-gray-400">○</span>
                                                    )}
                                                    <span className={currentUser.sellerCanReceivePayments ? "text-green-700 dark:text-green-300" : "text-gray-600 dark:text-gray-400"}>
                                                        Account details submitted
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    {currentUser.sellerCanReceivePayments ? (
                                                        <span className="text-green-600 dark:text-green-400">✓</span>
                                                    ) : (
                                                        <span className="text-gray-400">○</span>
                                                    )}
                                                    <span className={currentUser.sellerCanReceivePayments ? "text-green-700 dark:text-green-300" : "text-gray-600 dark:text-gray-400"}>
                                                        Payouts enabled
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )} */}

                            {/* Country Selector - Only show if account doesn't exist yet */}
                            {currentUser?.sellerOnboardingStatus && (
                                <div className="space-y-2">
                                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                                        🌍 Select Your Country
                                    </label>
                                    <select
                                        value={selectedCountry}
                                        onChange={(e) => setSelectedCountry(e.target.value)}
                                        disabled={loading || checkingStatus}
                                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-[#327691] focus:border-transparent outline-none transition disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {STRIPE_SUPPORTED_COUNTRIES.map((country) => (
                                            <option key={country.code} value={country.code}>
                                                {country.name}
                                            </option>
                                        ))}
                                    </select>
                                    {/* Show Paystack fields if ZA selected */}
                                    {(currentUser?.payoutMethod === "paystack" || selectedCountry === "ZA") && (
                                        <div className="mt-4 space-y-3">
                                            <div>
                                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                                                    🏦 Bank Name
                                                </label>
                                                <select
                                                    value={bankCode}
                                                    onChange={(e) => setBankCode(e.target.value)}
                                                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                                >
                                                    <option value="" disabled>Select a bank</option>
                                                    {
                                                        banks.map((bank) => (
                                                            <option
                                                                key={bank.code}
                                                                value={bank.code}
                                                            >
                                                                {bank.name}
                                                            </option>
                                                        ))
                                                    }
                                                </select>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                                                    💳 Account Number
                                                </label>
                                                <input
                                                    type="number"
                                                    value={accountNumber}
                                                    onChange={(e) => setAccountNumber(e.target.value)}
                                                    placeholder="Enter your account number"
                                                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                                />
                                            </div>
                                        </div>
                                    )}
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                        💡 Select the country where you'll be receiving payouts. Requirements vary by country.
                                    </p>
                                </div>
                            )}


                            {/* Detach Payout Method */}
                            {currentUser?.payoutMethod && (
                                <div className="mt-6 p-4 rounded-xl border border-red-400 bg-red-50 dark:bg-red-900/20 dark:border-red-700">
                                    <h4 className="text-red-700 dark:text-red-300 font-semibold mb-2">
                                        ⚠ Detach Payout Method
                                    </h4>
                                    <p className="text-sm text-red-600 dark:text-red-400 mb-3">
                                        Detaching will remove your current payout account. You will need to set it up again to receive payments.
                                    </p>
                                    <button
                                        onClick={handleDetachPayoutMethod}
                                        disabled={loading}
                                        className="flex-1 px-4 w-full py-3 rounded-xl font-semibold bg-red-600 text-white hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Detach Current Payout Method
                                    </button>
                                </div>
                            )}

                            {/* Switch Payout Method Section */}
                            {currentUser?.sellerOnboardingStatus === 'complete' && currentUser?.sellerCanReceivePayments && currentUser?.payoutMethod && (
                                <div className="mt-6 p-4 rounded-xl border border-yellow-400 bg-yellow-50 dark:bg-yellow-900/20 dark:border-yellow-700">
                                    <button
                                        onClick={handleSwitchPayoutMethod}
                                        disabled={loading || switchingMethod}
                                        className="w-full px-4 py-3 rounded-xl font-semibold bg-yellow-600 text-white hover:bg-yellow-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {currentUser.payoutMethod === "stripe" ? "Switch to Paystack" : "Switch to Stripe"}
                                    </button>
                                    <p className="mt-2 text-xs text-yellow-800 dark:text-yellow-300">
                                        ⚠ If you click "Switch", you will lose your current payout method.
                                    </p>
                                </div>
                            )}


                            {/* Information Cards */}
                            <div className="space-y-3">
                                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                                    <h4 className="font-semibold text-blue-800 dark:text-blue-200 mb-2">
                                        💰 How Payouts Work
                                    </h4>
                                    <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
                                        <li>• Connect your bank account securely via Stripe</li>
                                        <li>• Receive money directly when customers buy your products</li>
                                        <li>• Automatic deposits to your bank account</li>
                                        <li>• Track earnings in your Stripe Dashboard</li>
                                    </ul>
                                </div>

                                <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
                                    <h4 className="font-semibold text-purple-800 dark:text-purple-200 mb-2">
                                        🔒 Secure & Private
                                    </h4>
                                    <p className="text-sm text-purple-700 dark:text-purple-300">
                                        Your banking information is handled by Stripe, a trusted payment processor used by millions of businesses worldwide. We never see or store your bank details.
                                    </p>
                                </div>

                                <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                                    <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-2">
                                        📋 What You'll Need
                                    </h4>
                                    <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
                                        <li>• Government-issued ID</li>
                                        <li>• Social Security Number or Tax identification number (varies by country)</li>
                                        <li>• Bank account details for deposits</li>
                                        <li>• Business information (if applicable)</li>
                                    </ul>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                                        🌍 Available in 40+ countries worldwide
                                    </p>
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {/* Footer - stays fixed */}
                <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex gap-3 bg-white dark:bg-[#2A2A2A] flex-shrink-0 rounded-b-2xl">
                    <button
                        onClick={onClose}
                        disabled={loading}
                        className="flex-1 px-4 py-3 rounded-xl font-semibold border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Close
                    </button>

                    {/* Show "View Dashboard" only if payout is Stripe and setup complete */}
                    {currentUser?.sellerOnboardingStatus === 'complete' &&
                        currentUser?.sellerCanReceivePayments &&
                        currentUser?.payoutMethod === "stripe" ? (
                        <button
                            onClick={handleOpenDashboard}
                            disabled={loading}
                            className="flex-1 px-4 py-3 rounded-xl font-semibold bg-[#327691] text-white hover:bg-[#22516a] transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            {loading ? "Opening..." : (
                                <>
                                    <span>View Dashboard</span>
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                        />
                                    </svg>
                                </>
                            )}
                        </button>
                    ) :
                        currentUser?.sellerOnboardingStatus === 'complete' &&
                        currentUser?.sellerCanReceivePayments &&
                        currentUser?.payoutMethod === "paystack" &&
                        <button
                            onClick={async () => {
                                if (!bankCode || !accountNumber) {
                                    window.showToast("Please fill in both fields", "error");
                                    return;
                                }
                                handlePaystackOnboarding();
                            }}
                            disabled={loading}
                            className="flex-1 px-4 py-3 rounded-xl font-semibold bg-[#327691] text-white hover:bg-[#22516a] transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            <span>Update Details</span>
                        </button>
                    }

                    {/* Show "Start/Continue Setup" only if account not complete */}
                    {currentUser?.sellerOnboardingStatus !== 'complete' && (
                        <button
                            onClick={handleStartOnboarding}
                            disabled={loading || checkingStatus}
                            className="flex-1 px-4 py-3 rounded-xl font-semibold bg-[#327691] text-white hover:bg-[#22516a] transition disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading
                                ? "Starting..."
                                : currentUser?.sellerOnboardingStatus === 'incomplete'
                                    ? "Continue Setup"
                                    : "Start Setup"}
                        </button>
                    )}
                </div>

            </div>
        </div>
    );
};

export default SellerOnboarding;

